'use client';

import clsx from 'clsx';
import type { ReactNode } from 'react';

import styles from './SectionLoadingOverlay.module.scss';

export interface SectionLoadingOverlayProps {
  /** When true, covers children with a spinner and optional status label. */
  isLoading: boolean;
  /** Visible status text below the spinner */
  label?: string;
  /** When true, the overlay has no background scrim — only the spinner is shown. */
  transparent?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Section-level loading overlay: keeps layout in place, sets `aria-busy`, and announces
 * status via `role="status"`. Used during checkout address/tax sync.
 */
export function SectionLoadingOverlay({
  isLoading,
  label,
  transparent = false,
  children,
  className,
}: Readonly<SectionLoadingOverlayProps>) {
  return (
    <div className={clsx(styles.frame, className)} aria-busy={isLoading}>
      {children}
      {isLoading ? (
        <div
          className={clsx(styles.overlay, transparent && styles.overlayTransparent)}
          role="status"
          aria-live="polite"
        >
          <span className={styles.spinner} aria-hidden="true" />
          {label ? <span className={styles.label}>{label}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
